import {
  Badge,
  Button,
  Header,
  Table,
} from '@cloudscape-design/components';
import PPartDisplay from 'components/Projects/pPartDisplay';
import LotStatusBadge from 'features/lots/components/LotStatusBadge';
import PropTypes from 'prop-types';
import React from 'react';

/**
 * The scanned source bags of an assembly in progress. When the output
 * part has a BOM, each row shows how much of the bag this assembly will
 * use (from the greedy allocation) — or a warning badge for bags that
 * aren't on the BOM, which are allowed but consumed without a quantity.
 */
export default function AssembleSourcesTable({
  sources, bom, deltaByLotId, onRemove,
}) {
  const columnDefinitions = [
    {
      id: 'code',
      header: 'Code',
      cell: (item) => item.code,
    },
    {
      id: 'item',
      header: 'Item',
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: (item) => (
        <PPartDisplay
          fieldName="fullId"
          projectDocId={item.projectDocId}
          pPartDocId={item.pPartDocId}
          fallbackMessage="—"
        />
      ),
    },
    {
      id: 'status',
      header: 'Status',
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: (item) => <LotStatusBadge status={item.status} />,
    },
    {
      id: 'qty',
      header: 'Qty (remaining / initial)',
      cell: (item) => `${item.remainingQty} / ${item.initialQty}`,
    },
    ...(bom.length > 0 ? [{
      id: 'uses',
      header: 'This assembly uses',
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: (item) => {
        const onBom = bom.some((line) => line.projectDocId === item.projectDocId
          && line.pPartDocId === item.pPartDocId);
        if (!onBom) return <Badge color="severity-medium">Not on BOM</Badge>;
        return `${-(deltaByLotId[item.id] || 0)} of ${item.remainingQty}`;
      },
    }] : []),
    {
      id: 'remove',
      header: '',
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: (item) => (
        <Button
          iconName="close"
          variant="icon"
          ariaLabel={`Remove ${item.code}`}
          onClick={() => onRemove(item.id)}
        />
      ),
    },
  ];

  return (
    <Table
      header={<Header variant="h3" counter={`(${sources.length})`}>Scanned bags</Header>}
      items={sources}
      trackBy="id"
      columnDefinitions={columnDefinitions}
      variant="embedded"
      wrapLines
      empty={<div>Scan the source bags that go into this assembly</div>}
    />
  );
}

AssembleSourcesTable.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  sources: PropTypes.arrayOf(PropTypes.object).isRequired,
  bom: PropTypes.arrayOf(PropTypes.shape({
    projectDocId: PropTypes.string,
    pPartDocId: PropTypes.string,
    quantity: PropTypes.number,
  })).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  deltaByLotId: PropTypes.object.isRequired,
  onRemove: PropTypes.func.isRequired,
};
